/**
 * File drop zone for import functionality
 */
import clsx from 'clsx';
import type { DragEvent, ReactElement } from 'react';
import { useCallback, useState } from 'react';
import { IoCloudUpload, IoDocument } from 'react-icons/io5';

export interface FileDropzoneProps {
  onFileDrop: (file: File) => void;
  accept?: string;
  maxSize?: number;
  disabled?: boolean;
  description?: string;
  className?: string;
}

export default function FileDropzone({
  onFileDrop,
  accept = '.csv,.txt,.json',
  maxSize = 10 * 1024 * 1024, // 10MB
  disabled = false,
  description,
  className,
}: FileDropzoneProps): ReactElement {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateFile = useCallback(
    (file: File): string | null => {
      if (maxSize && file.size > maxSize) {
        return `File too large. Maximum size is ${Math.round(maxSize / 1024 / 1024)}MB`;
      }

      const acceptedTypes = accept.split(',').map((t) => t.trim().toLowerCase());
      const fileExt = '.' + file.name.split('.').pop()?.toLowerCase();
      const isAccepted = acceptedTypes.some((type) => type === fileExt || file.type.includes(type.replace('.', '')));

      if (!isAccepted) {
        return `Invalid file type. Accepted: ${accept}`;
      }

      return null;
    },
    [accept, maxSize],
  );

  const handleFile = useCallback(
    (file: File) => {
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }
      setError(null);
      onFileDrop(file);
    },
    [onFileDrop, validateFile],
  );

  const handleDragOver = useCallback(
    (e: DragEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {
        setIsDragging(true);
      }
    },
    [disabled],
  );

  const handleDragLeave = useCallback((e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (disabled) return;

      const file = e.dataTransfer.files[0];
      if (file) {
        handleFile(file);
      }
    },
    [disabled, handleFile],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFile(file);
      }
      // Reset input value so same file can be selected again
      e.target.value = '';
    },
    [handleFile],
  );

  return (
    <div className={className}>
      <label
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={clsx(
          'flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-8 transition-all duration-200',
          disabled && 'cursor-not-allowed opacity-50',
          isDragging ? 'border-aether bg-aether/10 scale-[1.02]' : 'border-storm hover:border-ash hover:bg-slate/50',
          error && 'border-danger/50',
        )}
      >
        <input type="file" accept={accept} onChange={handleInputChange} disabled={disabled} className="sr-only" />

        <div className={clsx('mb-4 rounded-full p-4 transition-colors', isDragging ? 'bg-aether/20 text-aether' : 'bg-slate text-ash')}>
          {isDragging ? <IoCloudUpload className="h-8 w-8 animate-bounce" /> : <IoDocument className="h-8 w-8" />}
        </div>

        <p className="text-pearl mb-1 text-center font-medium">{isDragging ? 'Drop file here' : 'Drop file or click to browse'}</p>
        <p className="text-ash text-center text-sm">
          {description ?? `Supports CSV, TXT, and JSON files (max ${Math.round(maxSize / 1024 / 1024)}MB)`}
        </p>
      </label>

      {error && <p className="text-danger mt-2 text-center text-sm">{error}</p>}
    </div>
  );
}
